import { PageController } from '@client/components/_core/layout/PageController';
import { VReadOnly } from '@client/components/_shared/forms/view-form/VReadOnly';
import { CardTitle } from '@client/components/_ui/display/CardTitle';
import { SkeletonBlock } from '@client/components/_ui/feedback/SkeletonBlock';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { createFormStore } from '@client/stores/FormStore';
import type { RouterOutputs } from '@client/trpc/client';
import { trpc } from '@client/trpc/client';
import { formatAddress } from '@shared/utils/core/formatting';
import { formatPhoneNumber } from '@shared/utils/core/phone-format';
import { createFileRoute } from '@tanstack/react-router';
import { Card } from 'antd';
import { Building2, CreditCard, HandCoins, User } from 'lucide-react';

export const Route = createFileRoute(
    '/console/$orgId/claims/details/$id/financials/compensations/details/$compensationId/payee',
)({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Payee info',
        },
    },

    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <Payee />
        </PageController>
    );
}

type Compensation = NonNullable<RouterOutputs['compensation']['getCompensation']>;

const PAYEE_TYPE_LABELS: Record<Compensation['payeeType'], string> = {
    POLICY_HOLDER: 'Policy holder',
    OTHER_CONTACT: 'Third party',
    SUPPLIER: 'Supplier',
};

const PAYMENT_METHOD_LABELS: Record<NonNullable<Compensation['paymentMethod']>, string> = {
    PAY_ID: 'PayID',
    BANK_ACCOUNT: 'Bank account',
};

const compensationFormStore = createFormStore<Compensation>();

function Payee() {
    const { compensationId } = Route.useParams();
    const { data, isPending, refetch } = trpc.compensation.getCompensation.useQuery({ id: compensationId });
    const { formatPercentage } = useFormatter();

    const { formData } = compensationFormStore.useInit({
        data,
        isLoading: isPending,
        isReadOnly: true,
        async onReload() {
            await refetch();
        },
        async onSave() {
            return { success: true };
        },
    });

    if (isPending) {
        return <SkeletonBlock variant="card" />;
    }

    if (!formData) {
        return null;
    }

    const isSupplier = formData.payeeType === 'SUPPLIER';
    const contact = formData.Contact;
    const supplier = formData.Supplier;

    return (
        <div className="grid grid-cols-2 gap-4">
            <Card
                type="inner"
                size="small"
                className="col-span-2"
                title={
                    <CardTitle
                        icon={isSupplier ? <Building2 /> : <User />}
                        title={isSupplier ? 'Supplier details' : 'Contact details'}
                    />
                }
                classNames={{ body: 'grid max-w-lg grid-cols-2 gap-x-6 gap-y-4' }}
            >
                <VReadOnly
                    label="Type"
                    field="payeeType"
                    formStore={compensationFormStore}
                    formatValueFn={(value) => PAYEE_TYPE_LABELS[value as Compensation['payeeType']]}
                />

                {isSupplier && supplier ? (
                    <>
                        <VReadOnly
                            label="Name"
                            formStore={compensationFormStore}
                            formatValueFn={(_, item) => item?.Supplier?.name}
                        />
                        <VReadOnly
                            label="Email"
                            formStore={compensationFormStore}
                            formatValueFn={(_, item) => item?.Supplier?.email}
                        />
                        <VReadOnly
                            label="Phone"
                            formStore={compensationFormStore}
                            formatValueFn={(_, item) => formatPhoneNumber(item?.Supplier?.phone)}
                        />
                        <div className="col-span-2">
                            <VReadOnly
                                label="Address"
                                formStore={compensationFormStore}
                                formatValueFn={(_, item) =>
                                    formatAddress({
                                        addressLine1: item?.Supplier?.addressLine1,
                                        addressLine2: item?.Supplier?.addressLine2,
                                        suburb: item?.Supplier?.suburb,
                                        state: item?.Supplier?.stateOrProvince,
                                        postcode: item?.Supplier?.postcode,
                                    }) || null
                                }
                            />
                        </div>
                        {contact && (
                            <VReadOnly
                                label="Contact person"
                                formStore={compensationFormStore}
                                formatValueFn={(_, item) =>
                                    [item?.Contact?.firstName, item?.Contact?.lastName].filter(Boolean).join(' ') ||
                                    null
                                }
                            />
                        )}
                    </>
                ) : (
                    contact && (
                        <>
                            <VReadOnly
                                label="Name"
                                formStore={compensationFormStore}
                                formatValueFn={(_, item) =>
                                    [item?.Contact?.firstName, item?.Contact?.lastName].filter(Boolean).join(' ') ||
                                    null
                                }
                            />
                            <VReadOnly
                                label="Email"
                                formStore={compensationFormStore}
                                formatValueFn={(_, item) => item?.Contact?.email}
                            />
                            <VReadOnly
                                label="Phone"
                                formStore={compensationFormStore}
                                formatValueFn={(_, item) => formatPhoneNumber(item?.Contact?.phone)}
                            />
                            <div className="col-span-2">
                                <VReadOnly
                                    label="Address"
                                    formStore={compensationFormStore}
                                    formatValueFn={(_, item) =>
                                        formatAddress({
                                            addressLine1: item?.Contact?.addressLine1,
                                            addressLine2: item?.Contact?.addressLine2,
                                            suburb: item?.Contact?.addressSuburb,
                                            state: item?.Contact?.addressState,
                                            postcode: item?.Contact?.addressPostcode,
                                        }) || null
                                    }
                                />
                            </div>
                        </>
                    )
                )}
            </Card>

            <Card
                type="inner"
                size="small"
                title={<CardTitle icon={<CreditCard />} title="Payment method" />}
                classNames={{ body: 'flex flex-col gap-4' }}
            >
                <VReadOnly
                    label="Payment method"
                    field="paymentMethod"
                    formStore={compensationFormStore}
                    formatValueFn={(value) =>
                        value ? PAYMENT_METHOD_LABELS[value as NonNullable<Compensation['paymentMethod']>] : null
                    }
                />

                {formData.paymentMethod === 'PAY_ID' && (
                    <VReadOnly label="PayID" field="payIdValue" formStore={compensationFormStore} />
                )}

                {formData.paymentMethod === 'BANK_ACCOUNT' && (
                    <>
                        <VReadOnly label="Account name" field="accountName" formStore={compensationFormStore} />
                        <VReadOnly label="BSB" field="accountBsb" formStore={compensationFormStore} />
                        <VReadOnly label="Account number" field="accountNo" formStore={compensationFormStore} />
                    </>
                )}
            </Card>

            <Card
                type="inner"
                size="small"
                title={<CardTitle icon={<HandCoins />} title="Tax information" />}
                classNames={{ body: 'grid max-w-lg grid-cols-2 gap-x-6 gap-y-4' }}
            >
                <div className="col-span-2">
                    <VReadOnly
                        label="Registered for GST"
                        field="isRegisteredForGst"
                        formStore={compensationFormStore}
                        formatValueFn={(value) => (value ? 'Yes' : 'No')}
                    />
                </div>
                <VReadOnly label="ABN" field="abn" formStore={compensationFormStore} />

                {!isSupplier && (
                    <VReadOnly
                        label="ITC entitlement"
                        field="inputTaxCreditEntitlement"
                        formStore={compensationFormStore}
                        formatValueFn={(value) => (value ? formatPercentage(Number(value) / 100) : null)}
                    />
                )}

                {isSupplier && (
                    <VReadOnly label="Remittance email" field="remittanceEmail" formStore={compensationFormStore} />
                )}
            </Card>
        </div>
    );
}
